import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Snackbar,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import MailOutlineIcon from "@mui/icons-material/MailOutline";

import { useNavigate } from "react-router-dom";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import AdminNavbar from "./admin/AdminNav";
import TalentNavbar from "./talent/NavigationTalent";
import RoleAlerts from "./talent/components/RoleAlerts";
import SearchFilters from "./components/SearchFilters";
import NoticesList from "./components/casting/NoticesList";

import api from "./api/api";
import { ENDPOINTS } from "./api/endpoints";
import Footer from "./Footer";
import AppLoader from "./components/common/AppLoader";
import GlobalNotification from "./components/GlobalNotification";

/* ---------- DEFAULT FILTER STATE ---------- */
const filtersInitialState = {
  location: [],
  ethnicity: [],
  unionStatus: [],
  jobCategories: [],
  roleTypes: [],
  sex: [],
  payType: [],
  ageMin: "",
  ageMax: "",
  specialSkills: "",
  vocalRange: [],
  vocalType: [],
  Video: false,
  Audio: false,
};

export default function CastingNoticesPage() {
  const navigate = useNavigate();
  const userRole = localStorage.getItem("userRole");
  const [userId, setUserId] = useState(null);

  const [filters, setFilters] = useState(filtersInitialState);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showExpired, setShowExpired] = useState(false);
  const [notices, setNotices] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [locationOptions, setLocationOptions] = useState([]);
  const [ethnicityOptions, setEthnicityOptions] = useState([]);
  const [unionOptions, setUnionOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [roleTypeOptions, setRoleTypeOptions] = useState([]);
  const [sexOptions, setSexOptions] = useState([]);
  const [paymentTypeOptions, setPaymentTypeOptions] = useState([]);
  const [activeTab, setActiveTab] = useState("Newest");

  // Plain div ref for reliable scroll targeting
  const noticesTopRef = useRef(null);

  // Toast
  const [toastOpen, setToastOpen] = useState(false);
  const [toastSeverity, setToastSeverity] = useState("success");
  const [toastMessage, setToastMessage] = useState("");

  const listTopRef = useRef(null);

  const showToast = (msg, severity = "success") => {
    setToastMessage(msg);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  // Reliable scroll: get the DOM node's offsetTop and use window.scrollTo
  const scrollToNotices = () => {
    if (noticesTopRef.current) {
      const top =
        noticesTopRef.current.getBoundingClientRect().top +
        window.pageYOffset -
        80; // 80px offset to account for fixed navbar
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  useEffect(() => {
    const id = localStorage.getItem("userId");
    if (id) setUserId(id);
  }, []);

  useEffect(() => {
    document.title = "Castings Calls";
    return () => {
      document.title = "Castings Calls";
    };
  }, []);

  const enhanceWithAny = (selected = [], options = [], anyLabel) => {
    if (!selected || selected.length === 0) return [];

    // If user explicitly selected "Any"
    if (selected.includes(anyLabel)) {
      return [anyLabel, ...options.map((o) => o.value)];
    }

    // Otherwise → include "Any" along with selected
    return [...new Set([...selected, anyLabel])];
  };

  const handleSaveSearch = () => {
    showToast("Save Search will be enabled soon", "info");
  };
const buildLocations = (selectedLocations = []) => {
  // No filter selected (default load)
  if (!selectedLocations.length) {
    return [];
  }

  // Nationwide selected
  if (selectedLocations.includes("NW")) {
    const allStateCodes = locationOptions
      .filter((l) => l.value !== "NW")
      .map((l) => l.value);

    return [...allStateCodes, "NW"];
  }

  // One or more specific states selected
  return [...new Set([...selectedLocations, "NW"])];
};
  /* ---------- BUILD PAYLOAD HELPER ---------- */
const buildPayload = (pageNumber, tab, userId, f) => ({
  Locations: buildLocations(f.location),
  Unions: f.unionStatus ?? [],
  JobCategories: f.jobCategories ?? [],
  Sex: enhanceWithAny(f.sex, sexOptions, "Any Gender"),
  Ethnicities: enhanceWithAny(f.ethnicity, ethnicityOptions, "Any Ethnicity"),
  RoleTypes: f.roleTypes ?? [],
  minAge: f.ageMin ? Number(f.ageMin) : null,
  maxAge: f.ageMax ? Number(f.ageMax) : null,
  page: pageNumber,
  pageSize: 50,
  userId: Number(userId),
  title: f.specialSkills || "",
  paymentType: null,
  PayLevels: f.payType?.includes("Any")
    ? paymentTypeOptions.filter((p) => p.value !== "Any").map((p) => p.value)
    : (f.payType ?? []),
  rushCall: tab === "RushCalls",
  favorite: tab === "Favourites",
  sortOrder: tab === "Newest" ? "DES" : null,
  startDate: "2000-01-01",
  endDate: new Date().toISOString().split("T")[0],
});
  /* ---------- SEARCH FUNCTION ---------- */
  const searchNotices = async (
    pageNumber = 1,
    tab = activeTab,
    currentUserId = userId,
    overrideFilters = null,
    scrollAfter = false,
  ) => {
    if (!currentUserId) return;

    setLoading(true);

    try {
      // snapshot filters (prevents mutation during render)
      const rawFilters = overrideFilters ?? filters;

      // sanitize mutated values
      const safeFilters = {
        ...filtersInitialState,
        ...rawFilters,

        // prevent slider defaults from becoming filters
        ageMin: rawFilters.ageMin || "",
        ageMax:
          rawFilters.ageMax === 100 || rawFilters.ageMax === 0
            ? ""
            : rawFilters.ageMax,

        // prevent select corruption ([], "", undefined → ignore)
        payType: Array.isArray(rawFilters.payType) ? rawFilters.payType : [],
      };

      const payload = buildPayload(pageNumber, tab, currentUserId, safeFilters);

      const res = await api.post(ENDPOINTS.getFilteredNotices(), payload);

      const raw = res.data;

      setNotices(raw?.data || []);
      setTotalPages(Math.ceil((raw?.count || 0) / 50));

      if (scrollAfter) {
        setTimeout(() => scrollToNotices(), 150);
      }
    } catch (err) {
      console.error(err);
      showToast("Failed to fetch casting notices", "error");
    } finally {
      setLoading(false);
    }
  };

  /* ---------- BACK TO ACTIVE - CLEAN FETCH ---------- */
  const fetchActiveNoticesClean = async (scrollAfter = false) => {
    if (!userId) return;
    setLoading(true);

    try {
      const payload = buildPayload(
        1,
        "RoleAlerts",
        userId,
        filtersInitialState,
      );
      const res = await api.post(ENDPOINTS.getFilteredNotices(), payload);
      const raw = res.data;
      setNotices(raw?.data || []);
      setTotalPages(Math.ceil((raw?.count || 1) / 50));
      if (scrollAfter) {
        setTimeout(() => scrollToNotices(), 150);
      }
    } catch (err) {
      console.error(err);
      showToast("Failed to fetch casting notices", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchExpiredNotices = async (pageNumber = 1, scrollAfter = false) => {
    if (!userId) return;
    setLoading(true);
    setNotices([]);

    try {
      const payload = {
        userId: userId,
        page: pageNumber,
        pageSize: 50,
      };

      const res = await api.post(
        ENDPOINTS.getExpiredFilteredNotices(),
        payload,
      );
      const raw = res.data;
      setNotices(raw?.data || []);
      setTotalPages(Math.ceil((raw?.count || 1) / 50));
      setShowExpired(true);

      if (scrollAfter) {
        setTimeout(() => scrollToNotices(), 150);
      }
    } catch (err) {
      console.error(err);
      showToast("Failed to fetch expired notices", "error");
    } finally {
      setLoading(false);
    }
  };

  /* ---------- TAB CHANGE HANDLER ---------- */
  const handleTabChange = (e, newValue) => {
    setActiveTab(newValue);
    setPage(1);
    setShowExpired(false);

    if (newValue === "RoleAlerts") {
      setFilters(filtersInitialState);
      searchNotices(1, newValue, userId, filtersInitialState);
    } else {
      searchNotices(1, newValue, userId);
    }
  };

  /* ---------- INITIAL LOAD ---------- */
  useEffect(() => {
    if (!userId) return;
    const load = async () => {
      setIsInitialLoading(true);
      try {
        const [locRes, ethRes, unionRes, catRes, payRes] = await Promise.all([
          api.get(ENDPOINTS.getLocations()),
          api.get(ENDPOINTS.getEthnicities()),
          api.get(ENDPOINTS.getUnions()),
          api.get(ENDPOINTS.getJobCategories()),
          api.get(ENDPOINTS.getPayTypes()),
        ]);

        const sortedLocations = (locRes.data.data || []).map((l) => ({
          value: l.locationCode,
          label:
            l.locationCity && l.locationCity !== l.locationState
              ? `${l.locationState.trim()} (${l.locationCity.trim()})`
              : l.locationState.trim(),
        }));

        // Float "Nationwide" (locationCode "NW") to the top of the list
        sortedLocations.sort((a, b) => {
          const aIsNationwide = a.value === "NW";
          const bIsNationwide = b.value === "NW";
          if (aIsNationwide && !bIsNationwide) return -1;
          if (!aIsNationwide && bIsNationwide) return 1;
          return 0;
        });

        setLocationOptions(sortedLocations);

        setEthnicityOptions(
          (ethRes.data.data || []).map((e) => ({
            value: e.ethnicityName,
            label: e.ethnicityName,
          })),
        );

        setUnionOptions(
          (unionRes.data || []).map((u) => ({
            value: u.affiliationName,
            label: u.affiliationName,
          })),
        );

        setCategoryOptions(
          (catRes.data.data || []).map((c) => ({
            value: c.jobCategoryName,
            label: c.jobCategoryName,
          })),
        );

        setRoleTypeOptions([
          { value: "Principal", label: "Principal" },
          { value: "Supporting", label: "Supporting" },
          { value: "Co-Star", label: "Co-Star" },
          { value: "Day Player", label: "Day Player" },
          { value: "Voice-Over", label: "Voice-Over" },
          { value: "Stunt", label: "Stunts" },
          { value: "Chorus/Ensemble", label: "Chorus/Ensemble" },
          { value: "Special Ability", label: "Special Ability" },
          { value: "Precision Driver", label: "Precision Driver" },
          { value: "Stand-In", label: "Stand-In" },
          { value: "Photo Double", label: "Photo Double" },
          { value: "Extras", label: "Extras" },
          { value: "N/A", label: "N/A" },
        ]);

        setSexOptions([
          { value: "Male", label: "Male" },
          { value: "Female", label: "Female" },
          { value: "Transfemale", label: "Trans Female" },
          { value: "Transmale", label: "Trans Male" },
          { value: "Non-Binary", label: "Non-Binary" },
        ]);

        const payOptions = (payRes.data.data || []).map((p) => ({
          value: String(p.payId),
          label: p.payType,
        }));

        setPaymentTypeOptions([{ value: "Any", label: "Any" }, ...payOptions]);

        await searchNotices(1, "RoleAlerts", userId, filtersInitialState);
      } catch (err) {
        console.error(err);
        showToast("Failed to load filters", "error");
      } finally {
        setIsInitialLoading(false);
      }
    };

    load();
  }, [userId]);

  /* ---------- UI ---------- */
  return (
    <div>
      {userRole === "ADMIN" ? <AdminNavbar /> : <TalentNavbar />}
      {userRole === "USER" && (
        <GlobalNotification userId={userId} userRole={userRole} />
      )}
      {isInitialLoading ? (
        <Box py={10} display="flex" justifyContent="center">
          <AppLoader message="Loading casting calls..." />
        </Box>
      ) : (
        <>
          <Box bgcolor="#fff" p={3}>
            <Accordion sx={{ my: 2, border: "1px solid #eee", mb: 4 }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ backgroundColor: "#eee" }}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <MailOutlineIcon sx={{ color: "#90b5fe" }} />
                  <Typography fontWeight="bold" fontSize="15px">
                    Get Role Alerts by Email
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails
                sx={{ backgroundColor: "#202020", color: "#fff" }}
              >
                <RoleAlerts userId={userId} showToast={showToast} />
              </AccordionDetails>
            </Accordion>
            <SearchFilters
              filters={filters}
              setFilters={setFilters}
              locationOptions={locationOptions}
              ethnicityOptions={ethnicityOptions}
              unionOptions={unionOptions}
              categoryOptions={categoryOptions}
              roleTypeOptions={roleTypeOptions}
              sexOptions={sexOptions}
              paymentTypeOptions={paymentTypeOptions}
              onSearch={() => {
                const snapshot = { ...filters };

                setNotices([]);
                setPage(1);

                searchNotices(1, activeTab, userId, snapshot, true);
              }}
              onClear={() => {
                setNotices([]);
                setPage(1);
                setFilters(filtersInitialState);

                searchNotices(1, activeTab, userId, filtersInitialState, true);
              }}
              handleSaveSearch={handleSaveSearch}
            />
          </Box>

          {/* Plain div as scroll anchor — more reliable than MUI Box for getBoundingClientRect */}
          <div ref={noticesTopRef} />

          <Box maxWidth="1200px" mx="auto" p={3} ref={listTopRef}>
            <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
              <Tab label="Newest" value="Newest" />
              <Tab label="Rush Calls" value="RushCalls" />
              <Tab label="Saved" value="Favourites" />
            </Tabs>
            <NoticesList
              notices={notices}
              loading={loading}
              page={page}
              totalPages={totalPages}
              onPageChange={(_, v) => {
                setPage(v);

                const snapshot = { ...filters };

                if (showExpired) {
                  fetchExpiredNotices(v, true);
                } else {
                  searchNotices(v, activeTab, userId, snapshot, true);
                }
              }}
              navigate={navigate}
            />

            <Box textAlign="center" mt={2}>
              {!showExpired ? (
                <Typography
                  sx={{
                    cursor: "pointer",
                    textDecoration: "underline",
                    color: "#1976d2",
                  }}
                  onClick={() => {
                    setNotices([]);
                    setPage(1);
                    fetchExpiredNotices(1, true);
                  }}
                >
                  View Expired Notices
                </Typography>
              ) : (
                <Typography
                  sx={{
                    cursor: "pointer",
                    textDecoration: "underline",
                    color: "#1976d2",
                  }}
                  onClick={async () => {
                    setShowExpired(false);
                    setNotices([]);
                    setPage(1);
                    setFilters(filtersInitialState);
                    setActiveTab("RoleAlerts");
                    await fetchActiveNoticesClean(true);
                  }}
                >
                  Back to Active Notices
                </Typography>
              )}
            </Box>
          </Box>
        </>
      )}

      <Snackbar
        open={toastOpen}
        autoHideDuration={4000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={toastSeverity} variant="filled">
          {toastMessage}
        </Alert>
      </Snackbar>

      <Footer />
    </div>
  );
}
